import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Download, X } from 'lucide-react';
import { usePWA } from '@/hooks/usePWA';
import { isDesktopApp } from '@/desktop/electronApi';
import type { PwaBrand } from '@/utils/pwaBranding';
import { resolveFileUrl } from '@/utils/fileUrl';

const DISMISS_KEY = 'pwa_install_bar_dismissed_v1';
const HIDE_ON_PATHS = ['/login', '/signup', '/unlock', '/reset-password', '/desktop-setup', '/customer-display'];

function isMobileViewport(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(max-width: 1023px)').matches || /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

type Props = {
  brand: PwaBrand;
};

/**
 * Sticky bottom bar on phones/tablets prompting PWA install.
 * Hidden when already installed, dismissed, auth screens, or Electron.
 */
export default function PwaMobileInstallBar({ brand }: Props) {
  const { t } = useTranslation();
  const location = useLocation();
  const [visibleViewport, setVisibleViewport] = useState(() => isMobileViewport());
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [showSteps, setShowSteps] = useState(false);

  const { isInstalled, installPWA, installError, showIosHelp, setShowIosHelp, platform } = usePWA({
    appName: brand.name,
    shortName: brand.shortName,
    logoUrl: brand.logoUrl,
  });

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const update = () => setVisibleViewport(isMobileViewport());
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);

  const onAuthScreen = HIDE_ON_PATHS.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`));
  // Keep POS sell screen clear — install prompt lives in Settings / account menu instead
  const onPosSellScreen = location.pathname === '/' || location.pathname === '';

  if (isDesktopApp() || isInstalled || dismissed || !visibleViewport || onAuthScreen || onPosSellScreen) {
    return null;
  }

  const logo = brand.logoUrl ? resolveFileUrl(brand.logoUrl) : '';
  const label = brand.shortName || brand.name;

  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* ignore */
    }
    setDismissed(true);
    setShowIosHelp(false);
    setShowSteps(false);
  };

  const onInstall = async () => {
    await installPWA();
    setShowSteps(true);
  };

  return (
    <div className="fixed bottom-0 inset-x-0 z-[60] p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pointer-events-none">
      <div className="pointer-events-auto mx-auto max-w-lg rounded-2xl border border-primary-200 bg-white shadow-lg shadow-primary-900/10 overflow-hidden">
        <div className="flex items-center gap-3 px-3 py-2.5">
          <div className="w-10 h-10 rounded-xl bg-gray-50 border border-gray-100 overflow-hidden shrink-0 flex items-center justify-center">
            {logo ? (
              <img src={logo} alt="" className="w-full h-full object-contain" />
            ) : (
              <Download className="w-4 h-4 text-primary-600" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-gray-900 truncate">{t('pwa.installName', { name: label })}</p>
            <p className="text-xs text-gray-500 truncate">{t('pwa.barSubtitle')}</p>
          </div>
          <button
            type="button"
            onClick={() => void onInstall()}
            className="shrink-0 bg-primary-600 hover:bg-primary-700 text-white text-xs font-semibold py-2 px-3 rounded-xl"
          >
            {t('pwa.install')}
          </button>
          <button
            type="button"
            onClick={dismiss}
            className="shrink-0 p-1.5 text-gray-400 hover:text-gray-600"
            aria-label={t('pwa.dismiss')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {(showSteps || showIosHelp || installError) && (
          <div className="border-t border-gray-100 bg-gray-50 px-3 py-2.5 text-xs text-gray-600">
            {installError && <p className="text-amber-700 mb-1">{installError}</p>}
            <p>{platform.startsWith('ios') ? t('pwa.barIosHint') : t('pwa.barAndroidHint')}</p>
          </div>
        )}
      </div>
    </div>
  );
}
